import PageHeader from "@/components/PageHeader";
import { useToast } from "@/components/ui/use-toast";
import { Module, PATH_ROOT, PATH_STORES } from "@/constants";
import { ApiError } from "@/services/api";
import { ReadStore, WriteStore } from "@/types/store";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import StoreForm from "./StoreForm";
import { useQueryCacheUpdater } from "@/hooks/useQueryCacheUpdater";
import { StoreService } from "../service/store";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";

export default function StoreCreator() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const { invalidateListQuery } = useQueryCacheUpdater(Module.Stores);
  const { can_create } = useModulePermissions();
  const breadcrumbItems = useMemo(
    () => [
      { label: t("breadcrumb.home"), href: PATH_ROOT },
      { label: t("stores.title.multiple"), href: PATH_STORES },
      { label: t("stores.new") },
    ],
    [t],
  );

  const mutateStore = async (values: WriteStore): Promise<ReadStore> =>
    StoreService.post(values);

  const storeMutation = useMutationWithErrorHandling<
    ReadStore,
    ApiError,
    WriteStore,
    unknown
  >({
    mutationFn: mutateStore,
    errorTitle: t("stores.errors.create"),
    onSuccess: (result) => {
      navigate(`${PATH_STORES}/${result.id}`);
      toast({
        title: t("stores.success.create"),
      });
      invalidateListQuery();
    },
  });

  return (
    <>
      <PageHeader breadcrumbItems={breadcrumbItems} />
      <StoreForm
        onSubmit={storeMutation.mutateAsync}
        disabled={storeMutation.isPending || !can_create}
      />
    </>
  );
}
